import React, { FC, useMemo } from "react";

import { ListPager } from "../../../components/pagination/ListPager";
import { usePagedRows } from "../../../components/pagination/usePagedRows";
import { getLinkedFirm } from "../../../sessionStorage/sessionStorage";
import { exportDataToExcel } from "../../../utils/ExportData";
import { askTable } from "../ask/reportAsk.utils";
import {
  TARGET_OPTIONS,
  underTarget,
  utilizationByPerson,
} from "../moreReports.utils";
import { formatHours, presetRange } from "../reports.utils";
import { scopeUserIds, useReportsAccess } from "../reportsAccess";
import { useViewExtra } from "../saved/SavedViewContext";
import { SplitBar } from "../ui/MoreCharts";
import {
  ChoiceMenu,
  DateMenu,
  PeopleClientTypeFilters,
  useReportFilters,
} from "../ui/ReportFilters";
import {
  Avatar,
  Card,
  Empty,
  Kpi,
  KpiGrid,
  Legend,
  LoadError,
  Loading,
  Note,
  ReportPage,
  useFocusRows,
} from "../ui/ReportUi";
import { useAllTime } from "../useReportData";

const TABLE = "Utilization by team member";
const TARGET_KEY = "proc_report_util_target";

/** The firm's last target (per firm, this browser), used when the report isn't a saved view. */
const storedTarget = () => {
  try {
    const all = JSON.parse(localStorage.getItem(TARGET_KEY) || "{}");
    const value = String(all[getLinkedFirm()?.orgId || ""] || "");
    return TARGET_OPTIONS.some((o) => o.value === value) ? value : "75";
  } catch {
    return "75";
  }
};
const storeTarget = (value: string) => {
  try {
    const all = JSON.parse(localStorage.getItem(TARGET_KEY) || "{}");
    all[getLinkedFirm()?.orgId || ""] = value;
    localStorage.setItem(TARGET_KEY, JSON.stringify(all));
  } catch {
    // Storage blocked: the target simply isn't remembered.
  }
};

/** Team utilization (2026-11-05): billable share of each person's tracked time vs. a target. */
export const UtilizationReport: FC = () => {
  const access = useReportsAccess();
  const state = useReportFilters("lastMonth");
  const { filters } = state;
  const [target, setTargetRaw] = useViewExtra(
    "target",
    storedTarget(),
    TARGET_OPTIONS,
    "Target",
  );
  const setTarget = (value: string) => {
    storeTarget(value);
    setTargetRaw(value);
  };
  const targetPct = Number(target) || 75;
  const range = useMemo(() => presetRange(filters.preset), [filters.preset]);
  const time = useAllTime({
    from: range?.from ?? null,
    to: range?.to ?? null,
    userIds: scopeUserIds(access, filters.userIds),
    customerIds: filters.clientIds,
    workTypeIds: filters.workTypeIds,
  });
  const rows = useMemo(() => utilizationByPerson(time.data), [time.data]);
  const billable = rows.reduce((s, r) => s + r.billable, 0);
  const nonBillable = rows.reduce((s, r) => s + r.nonBillable, 0);
  const total = billable + nonBillable;
  const firmPct = total > 0 ? Math.round((billable / total) * 100) : 0;
  const under = underTarget(rows, targetPct);
  const v = (x: React.ReactNode) => (time.isLoading ? "…" : x);

  const shown = useFocusRows(TABLE, rows, (r) => r.id);
  const { pageRows, pager } = usePagedRows(shown);

  const onExport = () =>
    exportDataToExcel(
      rows.map((r) => ({
        "Team member": r.name,
        "Billable hours": Math.round(r.billable * 100) / 100,
        "Non-billable hours": Math.round(r.nonBillable * 100) / 100,
        "Tracked hours": Math.round(r.total * 100) / 100,
        "Utilization (%)": r.pct,
        "Target (%)": targetPct,
      })),
      "Team utilization",
    );

  const ask = useMemo(
    () => ({
      title: "Team utilization",
      path: "/reports/utilization",
      description: `Billable share of each person's tracked time. Target ${targetPct}%.`,
      kpis: [
        { label: "Firm utilization", value: `${firmPct}%` },
        { label: "Target", value: `${targetPct}%` },
        { label: "Billable hours", value: formatHours(billable) },
        { label: "Non-billable hours", value: formatHours(nonBillable) },
      ],
      tables: [
        askTable(
          TABLE,
          rows,
          [
            {
              key: "person",
              label: "Team member",
              kind: "text",
              value: (r) => r.name,
            },
            {
              key: "billable",
              label: "Billable",
              kind: "hours",
              value: (r) => r.billable,
            },
            {
              key: "nonBillable",
              label: "Non-billable",
              kind: "hours",
              value: (r) => r.nonBillable,
            },
            {
              key: "total",
              label: "Tracked",
              kind: "hours",
              value: (r) => r.total,
            },
            {
              key: "pct",
              label: "Utilization",
              kind: "percent",
              value: (r) => r.pct,
            },
          ],
          (r) => r.id,
          (r) => `/works/all?userId=${r.id}`,
        ),
      ],
    }),
    [rows, firmPct, targetPct, billable, nonBillable],
  );

  return (
    <ReportPage
      title={"Team utilization"}
      description={
        "How much of each person's tracked time is billable, against the firm's target."
      }
      onExport={rows.length ? onExport : undefined}
      ask={time.isLoading ? undefined : ask}
      askSuggestions={[
        "Who is furthest under the target?",
        "How much non-billable time do we have?",
        "Is anyone over 90%?",
      ]}
      filters={
        <>
          <DateMenu
            value={filters.preset}
            onChange={(preset) => state.setFilters({ preset })}
          />
          <PeopleClientTypeFilters state={state} showPeople={access.firmWide} />
          <ChoiceMenu
            label={"Target"}
            options={TARGET_OPTIONS}
            value={target}
            onChange={setTarget}
          />
        </>
      }>
      {time.isError && <LoadError />}
      <KpiGrid>
        <Kpi
          label={"Firm utilization"}
          value={v(`${firmPct}%`)}
          note={`Target ${targetPct}%`}
          tone={firmPct < targetPct - 10 ? "amber" : "default"}
        />
        <Kpi
          label={"Billable hours"}
          value={v(formatHours(billable))}
          note={`of ${formatHours(total)} tracked`}
        />
        <Kpi
          label={"Non-billable"}
          value={v(formatHours(nonBillable))}
          note={"Admin, training, internal"}
        />
        <Kpi
          label={"Under target"}
          value={v(
            `${under.length} ${under.length === 1 ? "person" : "people"}`,
          )}
          note={"More than 10 points under"}
          tone={under.length ? "amber" : "default"}
        />
      </KpiGrid>

      <Card
        title={TABLE}
        right={
          <Legend
            items={[
              { label: "Billable", color: "#6B4EFF" },
              { label: "Non-billable", color: "#C9BEFF" },
              { label: `Target ${targetPct}%`, color: "#1F2433" },
            ]}
          />
        }>
        {time.isLoading ? (
          <Loading />
        ) : !rows.length ? (
          <Empty text={"No time tracked for these dates."} />
        ) : (
          <div className={"flex flex-col gap-1"}>
            {pageRows.map((r) => (
              <div
                key={r.id}
                className={
                  "grid items-center gap-3 border-b border-[#EDEFF3] py-2.5 last:border-b-0 sm:grid-cols-[200px_minmax(0,1fr)_56px_80px]"
                }>
                <span
                  className={
                    "flex min-w-0 items-center truncate text-[13.5px] font-bold"
                  }>
                  <Avatar name={r.name} color={r.color} />
                  {r.name}
                </span>
                <SplitBar
                  pct={r.pct}
                  target={targetPct}
                  label={`${r.name}: ${r.pct}% billable, target ${targetPct}%`}
                />
                <span
                  className={`text-right text-[13.5px] font-extrabold ${
                    r.pct < targetPct - 10 ? "text-[#B54708]" : "text-[#1F2433]"
                  }`}>
                  {r.pct}%
                </span>
                <span className={"text-right text-[13px] text-[#5B6472]"}>
                  {formatHours(r.total)}
                </span>
              </div>
            ))}
            <ListPager className={"px-1"} {...pager} />
          </div>
        )}
      </Card>

      <Note>
        Bars = share of each person&apos;s tracked time. Utilization = billable
        ÷ all tracked time. The target is remembered for your firm on this
        computer, or saved with the report when you use &quot;Save as my
        report&quot;.
        {!access.firmWide && " You see your own time."}
      </Note>
    </ReportPage>
  );
};
